import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

// Import the full project data to select featured projects from
import allProjectsData from '../data/allProjectsData';

// Select featured projects for the landing page - mix of statuses and categories
const featuredProjects = [
    // In Progress Projects
    allProjectsData.find(p => p.slug === "someseni-barc-st-1"), // Apartments in progress
    allProjectsData.find(p => p.slug === "duplex-iazului-floresti"), // Duplex in progress

    // Completed Projects
    allProjectsData.find(p => p.slug === "duplex-branduselor-floresti"), // Completed duplex
    allProjectsData.find(p => p.slug === "stejarului-street"), // Completed apartments
    allProjectsData.find(p => p.slug === "project-sesul-de-sus-190"), // Commercial space
    allProjectsData.find(p => p.slug === "ansamblu-duplexuri-urusagului") // Another completed duplex
].filter(Boolean); // Filter out any undefined projects (in case any slugs weren't found)

// ProjectCard Component with status badge
const ProjectCard = ({ project, index }) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });
    const [hovered, setHovered] = useState(false);
    const projectLink = `/projects/${project.slug || project.id}`;

    // Handle multiple categories for display
    const categoryDisplay = Array.isArray(project.category)
        ? project.category.join(' & ')
        : project.category;

    return (
        <Link to={projectLink} className="block group">
            <motion.div
                ref={ref}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative overflow-hidden rounded-lg shadow-lg bg-white h-full"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <div className="relative h-64 w-full">
                    <img
                        src={project.mainImage || project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => { e.target.onerror = null; e.target.src="/projects/placeholder_fallback.jpg" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-70 group-hover:opacity-80 transition-opacity duration-300"></div>

                    {/* Status badge */}
                    {project.status && (
                        <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium ${
                            project.status === 'Work In Progress'
                                ? 'bg-blue-500 text-white'
                                : 'bg-green-500 text-white'
                        }`}>
                            {project.status === 'Work In Progress' ? 'In Progress' : 'Completed'}
                        </div>
                    )}
                </div>

                <motion.div
                    className="absolute bottom-0 left-0 right-0 p-5 text-white"
                    initial={{ y: 30, opacity: 0 }}
                    animate={hovered ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    style={{ pointerEvents: 'none' }}
                >
                    <span className="text-arcdom-gold text-xs font-semibold mb-1 block uppercase tracking-wider">
                        {categoryDisplay} | {project.year}
                    </span>
                    <h3 className="text-lg font-bold mb-2 line-clamp-2">{project.title}</h3>
                    <div className="text-arcdom-gold inline-flex items-center text-sm font-medium">
                        View Project
                        <svg className="w-4 h-4 ml-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                </motion.div>

                <div
                    className={`absolute bottom-0 left-0 right-0 p-5 text-white transition-opacity duration-300 ${hovered ? 'opacity-0' : 'opacity-100'}`}
                    style={{ pointerEvents: 'none' }}
                >
                    <span className="text-arcdom-taupe text-xs font-semibold mb-1 block uppercase tracking-wider">
                        {categoryDisplay} | {project.year}
                    </span>
                    <h3 className="text-lg font-bold line-clamp-2">{project.title}</h3>
                </div>
            </motion.div>
        </Link>
    );
};

// Main Projects Component for Landing Page
const Projects = () => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <section id="projects" className="py-20 bg-gray-100">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl font-bold text-arcdom-navy mb-4">Featured Projects</h2>
                    <div className="w-20 h-1 bg-arcdom-gold mx-auto mb-6"></div>
                    <p className="max-w-2xl mx-auto text-gray-600">
                        A glimpse into our portfolio, showcasing expertise across various construction domains.
                    </p>
                </motion.div>

                {/* Projects Grid - Only showing featured projects */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {featuredProjects.length > 0 ? (
                        featuredProjects.map((project, index) => (
                            <ProjectCard key={project.id} project={project} index={index} />
                        ))
                    ) : (
                        <p className="text-center text-gray-600 md:col-span-2 lg:col-span-3">
                            No featured projects found.
                        </p>
                    )}
                </div>

                {/* View All Projects Button */}
                <div className="text-center mt-16">
                    <Link
                        to="/projects"
                        className="inline-flex items-center px-8 py-3 border-2 border-arcdom-navy text-arcdom-navy rounded-md font-semibold hover:bg-arcdom-navy hover:text-white transition-colors duration-300 text-lg"
                    >
                        View All Projects
                        <svg className="w-5 h-5 ml-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default Projects;
